import Close from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";
import { hoverStyle } from "components/helpers/MediaQueries";
import { states } from "components/helpers/ReusableLargeValues";
import handleUpdateDifferences from "components/helpers/UpdateFormDifferences";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useGetQuoteByIdQuery, useUpdateQuoteMutation } from "state/operationsApi";
import * as yup from 'yup';

const Form = ({ quote, closeForm }) => {
    const theme = useTheme();
    const [calculatedMargin, setCalculatedMargin] = useState(0);
    const [marginPercentage, setMarginPercentage] = useState(0);
    const [originStateValue, setOriginStateValue] = useState(states.find((state) => state.code === quote.origin.state));
    const [originInputValue, setOriginInputValue] = useState("");
    const [destinationStateValue, setDestinationStateValue] = useState(states.find((state) => state.code === quote.destination.state));
    const [destinationInputValue, setDestinationInputValue] = useState("");

    const [updateQuote] = useUpdateQuoteMutation();

    const { enqueueSnackbar } = useSnackbar();

    const formik = useFormik({
        initialValues: {
            emailSubject: quote.emailSubject,
            originCity: quote.origin.city,
            originState: quote.origin.state,
            destinationCity: quote.destination.city,
            destinationState: quote.destination.state,
            equipType: quote.equipType || "",
            loadType: quote.loadType || "",
            truckPrice: 0,
            customerQuote: 0,
            margin: 0,
            driverName: "",
            driverPhone: "",
            dispatcherName: "",
            dispatcherPhone: "",
            pickupAndDeliveryETA: "",
            shipmentDate: quote.shipmentDate || "",
            truckInHand: false,
            carrierMcNumber: "",
            carrierPhoneNumber: "",
            carrierEmail: "",
            notes: quote.notes || "",
            quoteStatus: quote.quoteStatus || "unknown",
        },
        validationSchema: yup.object({
            emailSubject: yup.string().required('Email subject is required'),
            originCity: yup.string().required('Origin city is required'),
            originState: yup.string().required('Origin state is required'),
            destinationCity: yup.string().required('Destination city is required'),
            destinationState: yup.string().required('Destination state is required'),
            equipType: yup.string(),
            loadType: yup.string(),
            truckPrice: yup.number(),
            customerQuote: yup.number(),
            margin: yup.number(),
            driverName: yup.string(),
            driverPhone: yup.string(),
            dispatcherName: yup.string(),
            dispatcherPhone: yup.string(),
            pickupAndDeliveryETA: yup.string(),
            shipmentDate: yup.string(),
            truckInHand: yup.boolean(),
            carrierMcNumber: yup.string(),
            carrierPhoneNumber: yup.string(),
            carrierEmail: yup.string().email('Invalid email address'),
            status: yup.string(),
            assignedName: yup.string(),
            assignedEmail: yup.string(),
            notes: yup.string(),
            quoteStatus: yup.string(),
        }),
        onSubmit: values => {
            handleQuoteFormSubmission(values);
        }
    })

    const handleQuoteFormSubmission = async (values) => {
        const newQuote = {
            emailSubject: values.emailSubject,
            origin: {
                city: values.originCity,
                state: values.originState
            },
            destination: {
                city: values.destinationCity,
                state: values.destinationState
            },
            equipType: values.equipType,
            loadType: values.loadType,
            truckPrice: values.truckPrice,
            customerQuote: values.customerQuote,
            margin: calculatedMargin,
            driverName: values.driverName,
            driverPhone: values.driverPhone,
            dispatcherName: values.dispatcherName,
            dispatcherPhone: values.dispatcherPhone,
            pickupAndDeliveryETA: values.pickupAndDeliveryETA,
            shipmentDate: values.shipmentDate,
            truckInHand: values.truckInHand,
            carrierInfo: {
                mcNumber: values.carrierMcNumber,
                phoneNumber: values.carrierPhoneNumber,
                email: values.carrierEmail
            },
            notes: values.notes,
            quoteStatus: values.quoteStatus,
        };
        const quoteUpdate = await handleUpdateDifferences(quote, newQuote);

        try {
            await updateQuote({ id: quote._id, updates: quoteUpdate })
                .then(() => {
                    enqueueSnackbar(`Quote Updated`);
                    closeForm(quote._id);
                })
        } catch (error) {
            console.error("Error updating quote:", error);
            enqueueSnackbar(`There was an error submitting the form`)
            
        }
    };

    const handleDecimalFormat = (value) => {
        return parseFloat(value).toFixed(2);
    };

    const handleQuoteChange = (e, value) => {
        let newMargin;

        if (value === "truckPrice") {
            formik.setFieldValue("truckPrice", e.target.value);
            newMargin = formik.values.customerQuote - e.target.value;
        } else if (value === "customerQuote") {
            formik.setFieldValue("customerQuote", e.target.value);
            newMargin = e.target.value - formik.values.truckPrice;
        };

        if (formik.values.truckPrice !== 0 && formik.values.customerQuote !== "0.00") {
            setCalculatedMargin((handleDecimalFormat(newMargin)));

            if (value === "truckPrice" && formik.values.truckPrice!== "0.00") {
                setMarginPercentage(handleDecimalFormat((newMargin / formik.values.customerQuote) * 100));
            } else if (value === "customerQuote" && formik.values.customerQuote !== 0) {
                setMarginPercentage(handleDecimalFormat((newMargin / e.target.value) * 100));
            };
        };
    };

    return (
        <>
            <DialogContent>
                <Container
                    component="main"
                    maxWidth="xl"
                    sx={{
                        paddingTop: 3,
                        backgroundColor: theme.palette.primary[900],
                    }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField 
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="emailSubject"
                                    label="Subject"
                                    name="emailSubject"
                                    value={formik.values.emailSubject}
                                    disabled
                                />
                                <Divider sx={{ m: '15px 0px', }} />
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container item spacing={2}>
                                    <Grid item xs={2}>
                                        <Typography>
                                            Origin:
                                        </Typography>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={10}>
                                        <TextField 
                                            margin="normal"
                                            fullWidth
                                            id="originCity"
                                            label="City"
                                            name="originCity"
                                            value={`${formik.values.originCity}, ${formik.values.originState}`}
                                            multiline
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Grid container item spacing={2}>
                                    <Grid item xs={2}>
                                        <Typography>
                                            Destination:
                                        </Typography>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={10}>
                                        <TextField 
                                            margin="normal"
                                            fullWidth
                                            id="destinationCity"
                                            label="City"
                                            name="destinationCity"
                                            value={`${formik.values.destinationCity}, ${formik.values.destinationState}`}
                                            multiline
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="quoteStatus"
                                    label="Quote Status"
                                    name="quoteStatus"
                                    value={formik.values.quoteStatus}
                                    disabled
                                    select
                                >
                                    <MenuItem value="live">Live</MenuItem>
                                    <MenuItem value="not live">Not Live</MenuItem>
                                    <MenuItem value="won">Won</MenuItem>
                                    <MenuItem value="unknown">Unknown</MenuItem>
                                </TextField>
                                {formik.touched.quoteStatus && formik.errors.quoteStatus && (
                                    <Alert severity="error">{formik.errors.quoteStatus}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="shipmentDate"
                                    label="Shipment Date"
                                    name="shipmentDate"
                                    value={formik.values.shipmentDate}
                                    disabled
                                />
                                {formik.touched.shipmentDate && formik.errors.shipmentDate && (
                                    <Alert severity="error">{formik.errors.shipmentDate}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="equipType"
                                    label="Equipment Type"
                                    name="equipType"
                                    value={formik.values.equipType}
                                    disabled
                                />
                                {formik.touched.equipType && formik.errors.equipType && (
                                    <Alert severity="error">{formik.errors.equipType}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="loadType"
                                    label="Load Type"
                                    name="loadType"
                                    value={formik.values.loadType}
                                    disabled
                                    defaultValue=""
                                    select
                                >
                                    <MenuItem value="" style={{ display: 'none', }} />
                                    <MenuItem value="Full Truckload">
                                        Full Truckload
                                    </MenuItem>
                                    <MenuItem value="Partial">
                                        Partial
                                    </MenuItem>
                                    <MenuItem value="Doesn't Matter">
                                        Doesn't Matter
                                    </MenuItem>
                                </TextField>
                                {formik.touched.loadType && formik.errors.loadType && (
                                    <Alert severity="error">{formik.errors.loadType}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="truckPrice">Truck Price</InputLabel>
                                    <OutlinedInput
                                        id="truckPrice"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Amount"
                                        name="truckPrice"
                                        type="number"
                                        value={formik.values.truckPrice}
                                        onChange={(e) => {
                                            handleQuoteChange(e, "truckPrice");
                                        }}
                                        onBlur={() => {
                                            if (formik.values.truckPrice !== "" || formik.values.truckPrice !== "0.00") {
                                                const newValue = handleDecimalFormat(formik.values.truckPrice);
                                                formik.setFieldValue("truckPrice", newValue);
                                            } else {
                                                formik.setFieldValue("truckPrice", 0);
                                            }
                                        }}
                                        onFocus={() => {
                                            if (formik.values.truckPrice === 0 || formik.values.truckPrice === "0.00") {
                                                formik.setFieldValue("truckPrice", "");
                                            }
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                'MozAppearance': 'textfield',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="customerQuote">Customer Quote</InputLabel>
                                    <OutlinedInput
                                        id="customerQuote"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Amount"
                                        name="customerQuote"
                                        type="number"
                                        value={formik.values.customerQuote}
                                        onChange={(e) => {
                                            handleQuoteChange(e, "customerQuote");
                                        }}
                                        onBlur={() => {
                                            if (formik.values.customerQuote !== "" || formik.values.customerQuote !== "0.00") {
                                                const newValue = handleDecimalFormat(formik.values.customerQuote);
                                                formik.setFieldValue("customerQuote", newValue);
                                            } else {
                                                formik.setFieldValue("customerQuote", 0);
                                            }
                                        }}
                                        onFocus={() => {
                                            if (formik.values.customerQuote === 0 || formik.values.customerQuote === "0.00") {
                                                formik.setFieldValue("customerQuote", "");
                                            }
                                        }}
                                        sx={{
                                            '& input[type=number]': {
                                                'MozAppearance': 'textfield',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                'WebkitAppearance': 'none',
                                                margin: 0,
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="margin">Calculated Margin</InputLabel>
                                    <OutlinedInput
                                        id="margin"
                                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                                        label="Amount"
                                        name="margin"
                                        value={calculatedMargin}
                                        disabled
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={1.5}>
                                <FormControl>
                                    <InputLabel htmlFor="margin">Margin %</InputLabel>
                                    <OutlinedInput
                                        id="margin"
                                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                                        label="Percentage"
                                        name="percentage"
                                        value={marginPercentage || 0}
                                        disabled
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4.5} />
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="carrierMcNumber"
                                    label="MC Number"
                                    name="carrierMcNumber"
                                    value={formik.values.carrierMcNumber}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.carrierMcNumber && formik.errors.carrierMcNumber && (
                                    <Alert severity="error">{formik.errors.carrierMcNumber}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="dispatcherName"
                                    label="Dispatcher Name"
                                    name="dispatcherName"
                                    value={formik.values.dispatcherName}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.dispatcherName && formik.errors.dispatcherName && (
                                    <Alert severity="error">{formik.errors.dispatcherName}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="dispatcherPhone"
                                    label="Dispatcher Phone Number"
                                    name="dispatcherPhone"
                                    value={formik.values.dispatcherPhone}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.dispatcherPhone && formik.errors.dispatcherPhone && (
                                    <Alert severity="error">{formik.errors.dispatcherPhone}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="carrierEmail"
                                    label="Dispatcher Email"
                                    name="carrierEmail"
                                    value={formik.values.carrierEmail}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.carrierEmail && formik.errors.carrierEmail && (
                                    <Alert severity="error">{formik.errors.carrierEmail}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="driverName"
                                    label="Driver Name"
                                    name="driverName"
                                    value={formik.values.driverName}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.driverName && formik.errors.driverName && (
                                    <Alert severity="error">{formik.errors.driverName}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    margin="normal"
                                    fullWidth
                                    id="driverPhone"
                                    label="Driver Phone Number"
                                    name="driverPhone"
                                    value={formik.values.driverPhone}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.driverPhone && formik.errors.driverPhone && (
                                    <Alert severity="error">{formik.errors.driverPhone}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="pickupAndDeliveryETA"
                                    label="Pickup and Delivery ETA"
                                    name="pickupAndDeliveryETA"
                                    value={formik.values.pickupAndDeliveryETA}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.pickupAndDeliveryETA && formik.errors.pickupAndDeliveryETA && (
                                    <Alert severity="error">{formik.errors.pickupAndDeliveryETA}</Alert>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id="truckInHand"
                                                name="truckInHand"
                                                checked={formik.values.truckInHand}
                                                onChange={() => {
                                                    formik.setFieldValue("truckInHand", !formik.values.truckInHand);
                                                }}
                                            />
                                        }
                                        label="Truck In Hand"
                                        sx={{ pt: '21px', }}
                                    />
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    id="notes"
                                    label="Notes for Operations"
                                    name="notes"
                                    multiline
                                    rows={4}
                                    value={formik.values.notes}
                                    onChange={formik.handleChange}
                                />
                                {formik.touched.notes && formik.errors.notes && (
                                    <Alert severity="error">{formik.errors.notes}</Alert>
                                )}
                            </Grid>
                        </Grid>
                    </form>
                </Container>
            </DialogContent>
            <DialogActions>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={formik.handleSubmit}
                >
                    Submit
                </Button>
            </DialogActions>
        </>
    )
}

const CustomerQuoteSubmissionForm = ({ open, closeForm, quoteId, onSubmit }) => {
    const { data: quote = null, isLoading: quoteLoading, error: quoteError } = useGetQuoteByIdQuery(quoteId);

    return (
        <Dialog open={open} onClose={closeForm} fullWidth maxWidth="lg">
            <DialogTitle>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Quote Submission Form</Typography>
                    <IconButton onClick={closeForm}>
                        <Close sx={hoverStyle}/>
                    </IconButton>
                </div>
                <Divider sx={{ mb: '15px' }}/>
            </DialogTitle>
            {quoteLoading ?
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </DialogContent>
            : quoteError ?
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography sx={{ mb: '25px' }}>There was an issue loading the customer quote. Quote may have been deleted by Operations.</Typography>
                </DialogContent>
            :
                <Form quote={quote.quote} closeForm={onSubmit} />
            }
        </Dialog>
    )
    
};

export default CustomerQuoteSubmissionForm;